import { useCallback, useEffect, useRef } from 'react'
import { useConfigStore } from '@/stores/useConfigStore'

/**
 * Feature keys for tracking first-time user interactions.
 * Add new features here as you build them.
 */
export type FeatureKey =
  | 'plannerView'
  | 'taskView'
  | 'calendarSidebar'
  | 'dayViewSidebar'
  | 'dragReorderTasks'
  | 'dragToCalendar'
  | 'accountSettings'
  | 'taskListSort'
  | 'layoutToggle'
  | 'addAccountButton'

interface UseFeatureDiscoveryOptions {
  /**
   * Delay in ms before automatically marking the feature as seen.
   * Set to `null` to disable auto-marking (require explicit markAsSeen call).
   * @default null (no auto-mark)
   */
  autoMarkDelay?: number | null
  /**
   * Callback fired when the feature is auto-marked as seen (after autoMarkDelay).
   * Use this to trigger actions like showing a modal, toast, or starting a tour.
   * Only called if autoMarkDelay is set and the user stays on the component.
   */
  onAutoMark?: () => void
}

interface UseFeatureDiscoveryReturn {
  /** True if the user has never seen this feature before */
  isFirstTime: boolean
  /** Call this to mark the feature as seen (e.g., on dismiss, click, or tour completion) */
  markAsSeen: () => void
  /** ISO timestamp of when the feature was first seen, or undefined if never seen */
  firstSeenAt: string | undefined
  /** Call this to reset the feature (allow the tour to show again) */
  reset: () => void
}

/**
 * Hook for tracking first-time user interactions with features.
 * Use this to show onboarding tooltips, tours, or highlights when users
 * encounter a feature for the first time.
 *
 * @example
 * // Basic usage - show tooltip on first visit
 * const { isFirstTime, markAsSeen } = useFeatureDiscovery('plannerView')
 * return (
 *   <>
 *     {isFirstTime && <Tooltip onDismiss={markAsSeen}>Welcome!</Tooltip>}
 *     <PlannerContent />
 *   </>
 * )
 *
 * @example
 * // Auto-mark after 3 seconds
 * const { isFirstTime } = useFeatureDiscovery('calendarSidebar', { autoMarkDelay: 3000 })
 *
 * @example
 * // Trigger action after user has been on page for 5 seconds
 * const { isFirstTime } = useFeatureDiscovery('plannerView', {
 *   autoMarkDelay: 5000,
 *   onAutoMark: () => setShowWelcomeModal(true)
 * })
 *
 * @example
 * // Highlight a button until clicked
 * const { isFirstTime, markAsSeen } = useFeatureDiscovery('dragToCalendar')
 * return (
 *   <Button
 *     onClick={() => { markAsSeen(); doAction() }}
 *     className={isFirstTime ? 'animate-pulse' : ''}
 *   />
 * )
 */
export const useFeatureDiscovery = (
  featureKey: FeatureKey,
  options: UseFeatureDiscoveryOptions = {}
): UseFeatureDiscoveryReturn => {
  const { autoMarkDelay = null, onAutoMark } = options

  const seenFeatures = useConfigStore((state) => state.config.seenFeatures)
  const markFeatureSeen = useConfigStore((state) => state.markFeatureSeen)
  const resetFeatureSeen = useConfigStore((state) => state.resetFeatureSeen)
  const isLoaded = useConfigStore((state) => state.isLoaded)

  const hasMarkedRef = useRef(false)
  const featureData = seenFeatures[featureKey]

  const isFirstTime = isLoaded && !featureData

  const markAsSeen = useCallback(() => {
    if (hasMarkedRef.current || !isFirstTime) return
    hasMarkedRef.current = true
    markFeatureSeen(featureKey)
  }, [featureKey, isFirstTime, markFeatureSeen])

  const reset = useCallback(() => {
    hasMarkedRef.current = false
    resetFeatureSeen(featureKey)
  }, [featureKey, resetFeatureSeen])

  // Auto-mark after delay if enabled
  useEffect(() => {
    if (!isFirstTime || autoMarkDelay === null) return

    const timer = setTimeout(() => {
      markAsSeen()
      onAutoMark?.()
    }, autoMarkDelay)

    return () => clearTimeout(timer)
  }, [isFirstTime, autoMarkDelay, markAsSeen, onAutoMark])

  return {
    isFirstTime,
    markAsSeen,
    firstSeenAt: featureData?.firstSeenAt,
    reset
  }
}

export default useFeatureDiscovery

